import { useEffect, useRef, useState } from "react";
import dataService from "../../../services/data.service";
import Http from "../../../services/Http";
import { useLocation } from "react-router-dom";
import { useHMG } from "../../../HMGContext";
import helper from "../../../services/helper";
import { HMG_REPROCESS_PROGRESS_URL } from "../../../constants/jobProgress";

// Within this window after the loader starts, a 100% reading is treated as a stale prior batch
// (an async reprocessing job may not have created its queue rows yet) and ignored.
const PROGRESS_GRACE_MS = 8000;
const POLL_INTERVAL_MS = 60000; // 1 minute

const HMGReprocessingLoader = () => {
  const [progress, setProgress] = useState(null);
  const [counts, setCounts] = useState({ total: 0, completed: 0, failed: 0 });
  const {
    selectedHMG,
    isReprocessingActive,
    setSelectedHMG,
    setIsReprocessingActive,
    setPlanTableRefreshTrigger,
  } = useHMG();
  const location = useLocation();
  const intervalRef = useRef(null);
  const startedAtRef = useRef(0);

  // Active when the server flagged the HMG as reprocessing (page-load) OR a flow triggered it live.
  const isActive = Boolean(dataService.getSelectedHMG()?.isReprocessing) || isReprocessingActive;

  // Done, but some plans failed: stop polling and keep the alert visible so the user can see the error.
  // Read-only is intentionally left engaged — the server still reports isReprocessing while failed
  // entries remain in the queue, so we don't change anything client-side.
  const completedWithFailures = progress != null && progress >= 100 && counts.failed > 0;

  useEffect(() => {
    if (!isActive) {
      return;
    }

    startedAtRef.current = Date.now();
    setProgress((prev) => (prev == null ? 0 : prev)); // show the bar immediately

    function fetchReprocessProgress() {
      Http.GET(
        HMG_REPROCESS_PROGRESS_URL(dataService.getSelectedHMGId()), {}, { _silent: true }
      ).then((res) => {
        if (res && res.status === 200 && res.data) {
          const newProgress = helper.getFloatValue(res.data.percentage);
          const total = res.data.total || 0;
          const failed = res.data.failed || 0;

          // Always reflect the current batch counts (so failures are visible, not hidden).
          if (total > 0) {
            setProgress(newProgress);
            setCounts({ total, completed: res.data.completed || 0, failed });
          }

          // Act on completion only after the grace window, so a stale prior batch reading 100%
          // right after trigger can't immediately reload before our batch's rows exist.
          if (newProgress === 100.0) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            if (failed === 0) {
              // All succeeded — release read-only and refresh data in place (no full page reload).
              // Flip the flag in both stores the read-only gates read from: the mutable HMG cache
              // (dataService) and the React context (selectedHMG). Clearing isReprocessingActive both
              // hides this loader and drives dependent effects/memos to re-evaluate.
              const cachedHmg = dataService.getSelectedHMG();
              if (cachedHmg) {
                cachedHmg.isReprocessing = false;
              }
              setSelectedHMG((prev) => (prev ? { ...prev, isReprocessing: false } : prev));
              setIsReprocessingActive(false);
              // Refresh the open plan-detail grid (edit-plans listens to this trigger).
              setPlanTableRefreshTrigger(new Date());
              // Notify page-local views (e.g. all-plans list) to refresh themselves.
              window.dispatchEvent(new CustomEvent("hmg-reprocessing-complete"));
              // Hide the loader.
              setProgress(null);
              setCounts({ total: 0, completed: 0, failed: 0 });
            }
            // If some failed, stop polling but keep the alert + read-only as-is (server still
            // reports isReprocessing). The user can read the error; nothing is changed.
          }
        }
      }).catch((e) => {
        // 403: no access to this HMG — stop so the global 403 toast doesn't repeat
        if (e?.response?.status === 403) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setProgress(null);
        }
      });
    }

    fetchReprocessProgress();

    // Then poll every minute
    intervalRef.current = setInterval(fetchReprocessProgress, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [location, selectedHMG, isActive]); // Re-run when route/HMG changes or the live trigger flips on

  if (!isActive) return null;

  if (progress == null) return null;

  return (
    <div className="p-4 bg-[#CAFDF5] shadow-[0px_4px_16px_0px_#00000040] rounded-lg max-w-md">

      <div>
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-[#637381] leading-[18px]">
            {completedWithFailures ? "Reprocessing completed with errors" : "Reprocessing"}
          </span>
        </div>

        <div className="flex items-center gap-3 justify-between my-1">
          <span className="text-[#212B36] text-sm leading-[22px]">
            {counts.total > 0
              ? `${counts.completed} / ${counts.total} plans completed`
              : `${progress}% completed`}
          </span>
        </div>

        {counts.failed > 0 && (
          <div className="my-1">
            <span className="text-[#B71D18] text-xs leading-[18px]">
              {counts.completed} succeeded, {counts.failed} failed
            </span>
          </div>
        )}

        {/* Progress Bar */}
        <div className="bg-white rounded-full h-2 w-[300px]">
          <div
            className="bg-[#00B8D9] h-2 rounded-full"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};
export default HMGReprocessingLoader;
